import { useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuthStore } from '../stores/auth.store';
import { api } from '../services/api';
import { useUILocale } from '../hooks/useUILocale';
import { withLocale } from '../locale';

const AccountRemovePage: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const uiLocale = useUILocale();
  const href = (p: string) => withLocale(uiLocale, p);

  const [step, setStep] = useState<'info' | 'confirm' | 'done'>('info');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      await api.deleteAccount();
      logout();
      setStep('done');
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'done') {
    return (
      <Layout title="Account Deleted">
        <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
          <span className="text-5xl mb-4 block">✅</span>
          <h2 className="text-xl font-bold mb-2">Account Deleted</h2>
          <p className="text-gray-500 mb-6">
            Your account and all associated data have been permanently deleted.
          </p>
          <a
            href={href("/")}
            className="text-primary-600 font-medium hover:underline"
          >
            Return to homepage
          </a>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Delete Account">
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="text-center mb-6">
            <span className="text-5xl mb-3 block">⚠️</span>
            <h2 className="text-xl font-bold">Delete Your Account</h2>
          </div>

          <div className="bg-red-50 rounded-xl p-4 mb-6">
            <h3 className="font-medium text-red-800 mb-2">This action will permanently delete:</h3>
            <ul className="text-sm text-red-700 space-y-1">
              <li>• Your account and profile</li>
              <li>• All stories you created</li>
              <li>• All your contributions to stories</li>
              <li>• Your email address from our system</li>
            </ul>
          </div>

          <p className="text-gray-500 text-sm mb-6">
            This action cannot be undone. If you're sure you want to delete your account,
            please sign in first and confirm below.
          </p>

          {!isAuthenticated ? (
            <div className="space-y-3">
              <p className="text-center text-gray-600">
                Please sign in to delete your account
              </p>
              <a
                href={href("/login")}
                className="block w-full bg-primary-600 text-white py-3 rounded-xl font-semibold text-center hover:bg-primary-700 transition"
              >
                Sign In
              </a>
            </div>
          ) : step === 'info' ? (
            <div className="space-y-3">
              <p className="text-center text-gray-600">
                Signed in as: <strong>{user?.email}</strong>
              </p>
              <button
                onClick={() => setStep('confirm')}
                className="w-full bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition"
              >
                I want to delete my account
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-center text-gray-600 font-medium">
                Are you absolutely sure?
              </p>
              {error && (
                <p className="text-red-500 text-center text-sm">{error}</p>
              )}
              <button
                onClick={handleDelete}
                disabled={loading}
                className="w-full bg-red-600 text-white py-3 rounded-xl font-semibold hover:bg-red-700 transition disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Yes, permanently delete my account'}
              </button>
              <button
                onClick={() => setStep('info')}
                disabled={loading}
                className="w-full text-gray-600 py-2 font-medium"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-gray-400">
          <a href={href("/privacy")} className="hover:underline">Privacy Policy</a>
        </p>
      </div>
    </Layout>
  );
};

export default AccountRemovePage;
